import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * Apoio comum das telas mobile de Ordens de serviço (`prototipos-mobile/ordens/*`).
 * Só apresentação: rótulos/cores de status do protótipo e formatação de datas.
 */

// Status reais da API -> rótulo do protótipo, tom do `.pill` e cor da faixa lateral.
const STATUS_VIEW = {
    'não iniciada': { label: 'Não iniciada', tone: 'warning', color: '#f59e0b', open: true },
    'em execução': { label: 'Em execução', tone: 'info', color: '#2563eb', open: true },
    iniciada: { label: 'Em execução', tone: 'info', color: '#2563eb', open: true },
    finalizada: { label: 'Finalizada', tone: 'success', color: '#16a34a', open: false },
    paga: { label: 'Faturada', tone: 'purple', color: '#8b5cf6', open: false },
    faturada: { label: 'Faturada', tone: 'purple', color: '#8b5cf6', open: false },
    cancelada: { label: 'Cancelada', tone: 'danger', color: '#ef4444', open: false },
};

export function orderStatusView(status) {
    return STATUS_VIEW[String(status || '').trim().toLowerCase()] || { label: status || '—', tone: undefined, color: '#94a3b8', open: false };
}

export const isOrderOpen = (status) => orderStatusView(status).open;

// A API grava a data/hora "como digitada" em UTC (ver `List.js`): lê os campos em UTC.
// Devolve null para vazio/inválido e para a data "zero" (0001-01-01).
export function orderMoment(value) {
    if (!value) return null;
    const parsed = dayjs.utc(value);
    if (!parsed.isValid() || parsed.year() < 1900) return null;
    return parsed;
}

const hourLabel = (m) => (m.minute() === 0 ? `${m.format('H')}h` : m.format('HH:mm'));

// Data do relógio local "de hoje" no mesmo formato dos campos UTC acima.
const todayStart = () => dayjs().startOf('day');
const dayOf = (m) => dayjs(m.format('YYYY-MM-DD'));

/**
 * Situação do prazo (entrega) de uma ordem: `bucket` p/ agrupar a lista
 * ("Atrasadas", "Hoje", "Esta semana"…), texto curto do calendário do cartão
 * ("Venceu 14/10", "Hoje 17h", "18/10") e o aviso do rodapé ("Prazo hoje").
 */
export function orderDeadline(order) {
    const m = orderMoment(order.delivery_date);
    const open = isOrderOpen(order.status);
    if (!m) return { bucket: 'none', label: '—', foot: '' };
    const today = todayStart();
    const day = dayOf(m);
    const diff = day.diff(today, 'day');
    if (diff === 0) return { bucket: 'today', label: `Hoje ${hourLabel(m)}`, foot: open ? 'Prazo hoje' : '' };
    if (diff < 0 && open) return { bucket: 'late', label: `Venceu ${m.format('DD/MM')}`, foot: '' };
    if (diff > 0 && day.isSame(today, 'week')) return { bucket: 'week', label: m.format('DD/MM'), foot: '' };
    if (diff < 0 && day.isSame(today, 'week')) return { bucket: 'week', label: m.format('DD/MM'), foot: '' };
    return { bucket: diff < 0 ? 'before' : 'after', label: m.format('DD/MM'), foot: '' };
}

export const BUCKET_ORDER = [
    { key: 'late', label: 'Atrasadas' },
    { key: 'today', label: 'Hoje' },
    { key: 'week', label: 'Esta semana' },
    { key: 'after', label: 'Mais adiante' },
    { key: 'before', label: 'Anteriores' },
    { key: 'none', label: 'Sem prazo' },
];

export function initials(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

// "dd/mm/aaaa" a partir de dayjs, ou '—'.
export const fmtDate = (m) => (m ? m.format('DD/MM/YYYY') : '—');
export const fmtShort = (m) => (m ? `${m.format('DD/MM')} · ${m.format('HH:mm')}` : '—');

// "84200" -> "84.200"
export function formatKm(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '—';
    return n.toLocaleString('pt-BR');
}

export const isZeroMoney = (value) => {
    if (value === undefined || value === null || value === '') return true;
    const n = parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
    return Number.isNaN(n) || n === 0;
};

// API: "Honda - Civic" -> "Honda Civic" (formato do protótipo)
export const vehicleLabel = (name) => (name && name !== '-' ? String(name).replace(/\s+-\s+/, ' ') : '');
